import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Input,
  Label,
  Container,
  CardFooter,
  Badge,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import AdminHeader from "components/Headers/AdminHeader";
import { useDispatch, useSelector } from "react-redux";
import { preRequisitesDocumentList } from "constants/preRequisitesDocument";
import {
  getPreRequisitesDocList,
  editPreRequistesDoc,
  addPreRequisitesDoc,
} from "action/preRequisitesDocument";
import { REGEX } from "constants/CommonConstants";

const initialFields = {
  documentName: {
    value: "",
    error: "",
    isChanged: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChanged: false,
  },
};

function PreRequiredDocument() {
  const dispatch = useDispatch();
  const preRequisitesListData = useSelector(
    (state) => state.preRequisitesDocument.preRequisitesDocumentList,
  );
  const { notify } = useContext(NotificationContext);
  const [isModalOpen, setIsModalOpen] = useState(null); //"add "edit" "null"
  const [formData, setFormData] = useState(initialFields);
  const [pageNum, setPageNum] = useState(1);
  const formattedListData = preRequisitesListData?.data?.prerequisiteDocuments?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
            (preRequisitesListData?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
        id: item?.ID,
      };
    },
  );

  const [searchState, setSearchState] = useState({
    documentName: "",
    status: null,
    filterParams: [],
  });
  const [loaderOnClick, setLoaderOnClick] = useState(false);
  const [preDocumentListState, setPreDocumentListState] = useState({
    isLoading: true,
    noRecords: false,
    error: "",
    isSearching: false,
    isUpdatingStatus: false,
  });

  useEffect(() => {
    getPreRequisitesDocList(dispatch, pageNum, searchState.filterParams);
    return () => {
      dispatch({ type: preRequisitesDocumentList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  //to check whether data get loaded or not using code or message
  useEffect(() => {
    if (preRequisitesListData?.code === 200) {
      setPreDocumentListState({
        isLoading: false,
        error: "",
        noRecords: preRequisitesListData?.data?.prerequisiteDocuments?.length === 0,
        isSearching: false,
      });
    } else if (preRequisitesListData?.code || preRequisitesListData?.message) {
      setPreDocumentListState({
        isLoading: false,
        error:
          preRequisitesListData?.message ||
          "Failed to load Prerequisites document list! Please try again.",
        noRecords: false,
        isSearching: false,
      });
    }
  }, [preRequisitesListData]);

  const openAddModal = () => {
    // e.stopPropagation();
    setIsModalOpen("add");
    setFormData(initialFields);
  };

  const openEditModal = (document) => {
    setIsModalOpen("edit");
    setFormData({
      id: document.ID, // for acessing the id
      documentName: { value: document.documentName, error: "" },
      status: { value: document.status, error: "" },
    });
  };

  const closeModal = () => {
    setIsModalOpen(null);
    setFormData({
      ...initialFields,
    });
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field.isChanged) // only changed fields
        .map(([key, field]) => [key, field.value]),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const isEdit = isModalOpen === "edit";
    const bodyData = isEdit
      ? getEditedBodyData(formData)
      : {
          documentName: formData.documentName.value,
          status: formData.status.value,
        };

    if (isEdit && Object.keys(bodyData).length === 0) {
      notify("info", "No changes detected to update.");
      return;
    }

    setLoaderOnClick(true);
    try {
      const res = isEdit
        ? await editPreRequistesDoc(formData.id, bodyData)
        : await addPreRequisitesDoc(bodyData);

      if (res?.code === 200) {
        notify(
          "success",
          res?.message ||
            `Prerequisites document ${isEdit ? "updated" : "added"} successfully`,
        );
        closeModal();
        pageNum === 1
          ? getPreRequisitesDocList(dispatch, pageNum, searchState.filterParams)
          : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message ||
            `Failed to ${isEdit ? "update" : "add"} prerequisites document`;

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message ||
          `Failed to ${isEdit ? "update" : "add"} prerequisites document`,
      );
    } finally {
      setLoaderOnClick(false);
    }
  };

  const toggleStatus = (document) => {
    const isActive = document.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this Prerequisite document?`,
      message: `Prerequisite document "${
        document.documentName
      }" will be marked as  ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setPreDocumentListState((prev) => ({
            ...prev,
            isUpdatingStatus: true,
          }));
          const res = await editPreRequistesDoc(document.ID, {
            status: newStatus,
          });
          if (res?.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );
            getPreRequisitesDocList(
              dispatch,
              pageNum,
              searchState.filterParams,
            );
          } else notify("danger", res?.message || "Failed to update status!");
        } catch (error) {
          notify("danger", error?.message || "Error while updating status");
        } finally {
          setPreDocumentListState((prev) => ({
            ...prev,
            isUpdatingStatus: false,
          }));
        }
      },
    });
  };

  const handleSearchNameChange = (e) => {
    const value = e.target.value;

    if (value === "" || REGEX.ALPHANUMERIC_SPACE_REGEX.test(value)) {
      setSearchState((prev) => ({
        ...prev,
        documentName: value,
      }));
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 50,
      maxWidth: 60,
    },
    {
      Header: "Document Name",
      accessor: "documentName",
      minWidth: 210,
      maxWidth: 210,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const isActive = row.original?.status === "ACTIVE";

        return (
          <div className="d-flex justify-content-start align-items-center">
            {/* EDIT */}
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() => openEditModal(row.original)}
              id={`edit-${row.original.ID}`}
            >
              <i className="fas fa-edit mr-1 table-action-info" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit-${row.original.ID}`}>
              Edit
            </UncontrolledTooltip>
            {/* Divider */}
            <span className="mr-1 text-muted">|</span>

            {/* Activate / Deactivate */}
            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${row.original.ID}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${
                  isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${row.original.ID}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const hasChanged = prev[field]?.value !== value;

      return {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
          isChanged: hasChanged,
        },
      };
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleClear = () => {
    setPreDocumentListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      documentName: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // filters for search
  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.documentName?.trim()) {
      filterParams.push({
        field: "DocumentName",
        condition: "like",
        filterValues: [searchState.documentName.trim()],
      });
    }

    if (searchState.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setPreDocumentListState((prev) => ({
        ...prev,
        isLoading: true,
        isSearching: true,
      }));
      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const getValidationError = (documentName, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (documentName) {
      case "documentName":
        if (!trimmedValue) return "Please enter  prerequisites document name";
        if (value.trim().length < 5)
          return "Prerequisites document name must be at least 5 characters";
        if (value.trim().length > 150)
          return "Prerequisites document name cannot exceed 150 characters";
        // if (!REGEX.ALPHANUMERIC_SPACE_REGEX.test(trimmedValue))
        //   return " Prerequisites document name can contain only letters and numbers";
        return "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    let newDocument = { ...formData };

    Object.keys(newDocument).forEach((key) => {
      if (key === "id") return;

      newDocument[key] = {
        ...newDocument[key],
        error: getValidationError(key, newDocument[key].value),
      };
    });

    setFormData(newDocument);

    return Object.entries(newDocument)
      .filter(([key]) => key !== "id")
      .every(([, item]) => item.error === "");
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  return (
    <>
      <AdminHeader />
      {preDocumentListState.isUpdatingStatus && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Updating status... Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
             <CardTitle tag="h3" className="mb-0 text-default">
              Prerequisite Documents
            </CardTitle>

            <Button className="btn-deep-blue" onClick={openAddModal}>
              <i className="fa fa-plus mr-1" />
              Prerequisite Document
            </Button>
          </CardHeader>
          {/* Body */}
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
              {/* Left side - Filter Inputs + Search */}
              <div className="d-flex flex-wrap align-items-center filter-group">
                <Input
                  type="text"
                  placeholder="Search by document name"
                  className="mr-2 mb-2 filter-input-large"
                  value={searchState.documentName}
                  onChange={handleSearchNameChange}
                  onKeyDown={handleKeyDown}
                />
                <Select
                  className="mr-2 mb-2 filter-select"
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                  classNamePrefix="react-select"
                  value={searchState.status}
                  onChange={(opt) =>
                    setSearchState((prev) => ({
                      ...prev,
                      status: opt,
                    }))
                  }
                  onKeyDown={handleKeyDown}
                  styles={fixedWidthCustomStyle()}
                  components={{ Option, SingleValue }}
                />

                <Button
                  color="default"
                  // className="d-flex align-items-center btn-deep-blue mb-0"
                  className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${preDocumentListState.isSearching ? "search-btn" : ""}`}
                  onClick={handleSearch}
                >
                  {preDocumentListState.isSearching ? (
                    <i className="fa fa-spinner fa-spin loader" /> //loader
                  ) : (
                    <>
                      <i className="fa fa-search mr-1" />
                      Search
                    </>
                  )}
                </Button>

                {searchState.filterParams.length > 0 && (
                  <Button
                    color="default"
                    className="mb-2 filter-btn btn-deep-blue btn-simple"
                    onClick={handleClear}
                  >
                    <i className="fa fa-times mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 mb-3 mx-1">
              {preRequisitesListData?.data?.totalRecords ? (
                <>
                  {searchState.filterParams?.length > 0 ? (
                    <span className="total-records">
                      Showing{" "}
                      {preRequisitesListData?.data?.totalFilteredRecords} of{" "}
                      {preRequisitesListData?.data?.totalRecords} Records
                    </span>
                  ) : (
                    <span className="total-records">
                      Total Records :{" "}
                      {preRequisitesListData?.data?.totalRecords}
                    </span>
                  )}
                </>
              ) : null}
            </div>

            {preDocumentListState.isLoading ? (
              <div className="loaderContainer">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : preDocumentListState.error ? (
              <>
                <div className="loaderContainer error-style">
                  <div className="error-icon">
                    <i class="fa-solid fa-circle-exclamation"></i>
                  </div>
                  {preDocumentListState.error}
                </div>
              </>
            ) : preDocumentListState.noRecords ? (
              <div className="loaderContainer empty-style">
                <div className="empty-icon">
                  <i class="fa-solid fa-circle-info"></i>
                </div>
                <span>No prerequisite documents found.</span>
              </div>
            ) : (
              <CommonTable
                data={formattedListData || []}
                columns={columns}
                pageNum={pageNum}
                activePage={pageNum}
                setPageNum={setPageNum}
                defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                showPaginationBottom
                totalPages={Math.ceil(
                  preRequisitesListData?.data?.totalFilteredRecords /
                    preRequisitesListData?.data?.noOfRecordsPerPage ||
                    DEFAULT_RECORDS_PER_PAGE,
                )}
              />
            )}
          </CardBody>
        </Card>

        <RightPanelCard isOpen={isModalOpen !== null} onClose={closeModal}>
          <Card>
            <CardHeader tag="div" className="p-3">
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="d-flex align-items-center">
                  <i
                    className="fa-solid fa-angle-left cursor mr-2"
                    onClick={closeModal}
                  ></i>
                  <h4 className="mb-0 text-default">
                    {isModalOpen === "add"
                      ? "Add Prerequisites Document"
                      : "Edit Prerequisites Document"}
                  </h4>
                </div>

                <div className="d-flex justify-content-end">
                  <i
                    className="fa-solid fa-xmark cursor text-muted"
                    onClick={closeModal}
                  ></i>
                </div>
              </div>
            </CardHeader>

            <Form onSubmit={(e) => e.preventDefault()}>
              <CardBody>
                <Row>
                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Prerequisites Document Name{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="documentName"
                        type="text"
                        placeholder="Enter prerequisites document name"
                        value={formData.documentName.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={formData.documentName.error ? "is-invalid" : ""}
                        minLength={5}
                        maxLength={150}
                      />
                      {formData.documentName.error && (
                        <small className="invalid-feedback">
                          {formData.documentName.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Status <span className="text-danger"> *</span>
                      </Label>
                      <div className="radio-rapper mt-2">
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="ACTIVE"
                              checked={formData.status.value === "ACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Active
                          </Label>
                        </FormGroup>
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="INACTIVE"
                              checked={formData.status.value === "INACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Inactive
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>

              <CardFooter className="text-right">
                <Button
                  color="secondary"
                  onClick={closeModal}
                  className="save-btn-sm"
                >
                  Cancel
                </Button>
                <Button
                  color="default"
                  className="btn-deep-blue save-btn-sm"
                  disabled={loaderOnClick}
                  onClick={handleSubmit}
                >
                  {loaderOnClick ? (
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default PreRequiredDocument;
